import React, { createContext, FC, useContext } from 'react';
import {
  GlossaryTermList,
  GlossarySubTitle,
  GlossaryListItem,
  GlossaryTermListLink,
  GlossaryTermListItem,
} from 'components/Glossary/Glossary.style';
import GlossaryLoader from 'components/utility/ContentLoader/GlossaryLoader/GlossaryLoader';
import {
  ContentResult,
  ResultData,
  TopicResults,
} from './BrowseGlossary.types';
import { GlossaryNoResults } from '../Glossary.style';

interface IResults {
  results: ContentResult[];
}

interface IBrowseResults {
  data: ResultData[];
  modal: boolean;
  searchedWord?: string;
}

interface IRenderTopics {
  category: string;
  topics: TopicResults[];
  modal: boolean;
}

/** RenderContents renders the content results */
const RenderContents: FC<IResults> = ({ results }) => {
  const searchedWord = useContext(SearchedWordContext);
  return (
    <ul>
      {results.map((result) => (
        <li key={result.id}>
          ... <b>{searchedWord}</b>
          {result.content} ...
        </li>
      ))}
    </ul>
  );
};

/** renderTopics renders the links in each group of links in the Glossary */
const RenderTopics: FC<IRenderTopics> = ({ category, topics, modal }) => {
  return (
    <GlossaryTermList>
      {topics.map((topic) => (
        <GlossaryTermListItem key={topic.slug}>
          <GlossaryTermListLink
            to={{
              pathname: `/glossary/${category}/${topic.slug}`,
              state: {
                modal,
              },
            }}
          >
            {topic.name}
          </GlossaryTermListLink>
          {topic.results && <RenderContents results={topic.results} />}
        </GlossaryTermListItem>
      ))}
    </GlossaryTermList>
  );
};

/** renderGroups renders groups of links to GlossaryTerm pages */
const RenderGroups: FC<IBrowseResults> = ({ data, modal }) => {
  return (
    <ul>
      {data ? (
        data.map((item) => {
          return (
            <GlossaryListItem key={item.name}>
              <GlossarySubTitle>{item.name}</GlossarySubTitle>
              {item.topics && (
                <RenderTopics
                  category={item.slug}
                  topics={item.topics}
                  modal={modal}
                />
              )}
            </GlossaryListItem>
          );
        })
      ) : (
        <GlossaryListItem>
          <GlossaryLoader />
        </GlossaryListItem>
      )}
    </ul>
  );
};

const SearchedWordContext = createContext('');

const BrowseResults: FC<IBrowseResults> = ({ data, modal, searchedWord }) => {
  return (
    <>
      {data?.length === 0 ? (
        <GlossaryNoResults>{`Sorry, we couldn't find any results.`}</GlossaryNoResults>
      ) : (
        <SearchedWordContext.Provider value={searchedWord ?? ''}>
          <RenderGroups data={data} modal={modal} />
        </SearchedWordContext.Provider>
      )}
    </>
  );
};

export default BrowseResults;
